/** @file Weights layer: per-vertex heatmap (viridis) for one bone, or dominant-bone colours, per mesh. */
import type { MeshAttachmentData, SkeletonData } from '@pose2d/core/types';
import type { Sink } from '../draw/createSink';
import { emitTriangle } from '../draw/emitTriangle';
import { viridis } from '../draw/viridis';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { meshPixels } from './meshPixels';
import { meshScratch } from './meshScratch';

/** Heatmap alpha byte (the skeleton stays visible underneath). */
const ALPHA = 180;
const xy = new Float64Array(6);
const lights = new Uint32Array(3);

/** Bone index for a `weights` value: a bone name, else a decimal index; -1 when neither exists. */
function resolveBone(data: SkeletonData, value: string): number {
  const named = data.boneIndex.get(value);
  if (named !== undefined) return named;
  const index = /^\d+$/.test(value) ? Number(value) : -1;
  return index < data.bones.length ? index : -1;
}

/** True for a real bone index of the skeleton. */
const isBone = (bone: number, boneCount: number): boolean => bone >= 0 && bone < boneCount;

/**
 * Packed colour of a vertex from its (heaviest) bone and its weight on `target`: viridis for a target >= 0, else
 * (dominant, target -1) the bone's colour, or 0 = leave the vertex unpainted for an invalid bone (R7-03).
 */
function paint(target: number, boneCount: number, bone: number, weight: number): number {
  if (target >= 0) return viridis(weight, ALPHA);
  if (!isBone(bone, boneCount)) return 0;
  return (((OVERLAY_STYLE.dominant[bone % OVERLAY_STYLE.dominant.length] ?? 0) & 0xffffff) | (ALPHA << 24)) >>> 0;
}

/**
 * Colours of a skinned mesh's vertices into `colors[0, length)`: weight of `target`, or the heaviest valid influence
 * (first wins a tie). `count` = uvs.length / 2 (the loop runs as before; a fractional last vertex is not stored).
 */
function skinnedColors(colors: Uint32Array, skin: Extract<MeshAttachmentData['skin'], { kind: 'skinned' }>, count: number, length: number, target: number, boneCount: number): void {
  const { counts, bones, weights } = skin;
  for (let v = 0, k = 0; v < count; v++) {
    let best = -1, bestW = -1, mine = 0;
    for (const end = k + (counts[v] ?? 0); k < end; k++) {
      const b = bones[k] ?? -1, w = weights[k] ?? 0;
      if (b === target) mine += w;
      if (isBone(b, boneCount) && w > bestW) {
        best = b;
        bestW = w;
      }
    }
    if (v < length) colors[v] = paint(target, boneCount, best, mine) >>> 0;
  }
}

/**
 * Colour per vertex (0 = unpainted) in meshScratch's buffer, valid until the next call: weight of `target` through
 * viridis, or (target -1) the heaviest bone's colour. Its first trunc(uvs.length / 2) entries are the mesh's, every
 * later one reads 0, as a read past the array of that length allocated per mesh before.
 */
function vertexColors(mesh: MeshAttachmentData, slotBone: number, target: number, boneCount: number): Uint32Array {
  const count = mesh.uvs.length / 2, length = Math.trunc(count);
  const buffers = meshScratch.forMesh(mesh.uvs.length, mesh.triangles.length), colors = buffers.colors;
  if (mesh.skin.kind === 'rigid') colors.fill(paint(target, boneCount, slotBone, slotBone === target ? 1 : 0) >>> 0, 0, length);
  else skinnedColors(colors, mesh.skin, count, length, target, boneCount);
  if (buffers.colorsUsed > length) colors.fill(0, length, buffers.colorsUsed);
  buffers.colorsUsed = length;
  return colors;
}

/** Emits every triangle of the mesh with its three vertex colours; a triangle with an unpainted vertex is skipped. */
function fillMesh(sink: Sink, mesh: MeshAttachmentData, px: Float64Array, vertexLights: Uint32Array): void {
  const tris = mesh.triangles;
  for (let t = 0; t + 2 < tris.length; t += 3) {
    let painted = true;
    for (let k = 0; k < 3; k++) {
      const v = tris[t + k] ?? 0;
      xy[2 * k] = px[2 * v] ?? NaN;
      xy[2 * k + 1] = px[2 * v + 1] ?? NaN;
      lights[k] = vertexLights[v] ?? 0;
      painted &&= lights[k] !== 0;
    }
    if (painted) emitTriangle(sink, xy, lights);
  }
}

/** Heatmap of the mesh shown by `slot` (nothing for regions and empty slots). */
function drawSlot(sink: Sink, input: OverlayInput, slot: number, target: number): void {
  const { data, pose } = input;
  const att = pose.slotAttachment[slot] ?? -1;
  const mesh = data.attachments[att];
  const px = meshPixels(input, slot, att);
  if (mesh?.type !== 'mesh' || px === null) return;
  const override = pose.slotBoneOverride[slot] ?? -1;
  fillMesh(sink, mesh, px, vertexColors(mesh, override >= 0 ? override : (data.slots[slot]?.bone ?? -1), target, data.bones.length));
}

/** `options.weights`: 'dominant' or a bone name/index; unknown bones draw nothing (parseOverlays reports them). */
export function drawWeights(sink: Sink, input: OverlayInput): void {
  const value = input.options.weights;
  if (value === undefined) return;
  const target = value === 'dominant' ? -1 : resolveBone(input.data, value);
  if (value !== 'dominant' && target < 0) return;
  const order = input.pose.drawOrder;
  for (let k = 0; k < order.length; k++) drawSlot(sink, input, order[k] ?? -1, target);
}
